import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { usePermissionRegistry } from "@/hooks/use-permission-registry";
import { Badge, SkeletonTable } from "@/components/ui";
import {
  tokenKey,
  crudColumns,
  actionVerbs,
  domainStats,
  domainIsReadOnly,
  type DomainGroup,
  type EntityDescriptor,
} from "@/components/user-groups/permission-matrix-model";

type EffectivePermissions = components["schemas"]["EffectivePermissions"];

/**
 * Read-only row: the SAME CRUD-columns + action-verb layout as the interactive
 * group matrix, but every checkbox is disabled and every verb is a static pill
 * (no grant/revoke — the member's grants are DERIVED from group membership).
 */
const ReadOnlyEntityRow = memo(function ReadOnlyEntityRow({
  entity,
  granted,
}: {
  entity: EntityDescriptor;
  granted: ReadonlySet<string>;
}) {
  const { t } = useTranslation(["userGroups"]);
  const verbs = actionVerbs(entity);
  return (
    <tr className="border-t border-gray-100 dark:border-gray-800">
      <td className="px-4 py-2">
        <div className="font-medium text-on-surface">
          {t(`entity.${entity.entity}`, { defaultValue: entity.label })}
        </div>
        <div className="font-mono text-xs text-muted">{entity.entity}</div>
      </td>
      {crudColumns(entity).map(({ op, legal }) => {
        const on = granted.has(tokenKey(entity.entity, op));
        return (
          <td key={op} className="px-2 py-2">
            {legal ? (
              <input
                type="checkbox"
                className="h-4 w-4 cursor-default accent-[var(--color-primary)] opacity-70"
                checked={on}
                disabled
                readOnly
                aria-label={tokenKey(entity.entity, op)}
              />
            ) : (
              <span
                className="text-muted"
                title={t("matrix.illegalTitle", { operation: op, entity: entity.entity })}
              >
                —
              </span>
            )}
          </td>
        );
      })}
      <td className="px-4 py-2">
        {verbs.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {verbs.map((op) => {
              const on = granted.has(tokenKey(entity.entity, op));
              return (
                <span
                  key={op}
                  aria-label={tokenKey(entity.entity, op)}
                  aria-pressed={on}
                  className={`rounded-full border px-2.5 py-0.5 text-xs opacity-80 ${
                    on
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-gray-300 text-muted dark:border-gray-600"
                  }`}
                >
                  {t(`operation.${op}`, { defaultValue: op })}
                </span>
              );
            })}
          </div>
        )}
      </td>
    </tr>
  );
});

const ReadOnlyDomainSection = memo(function ReadOnlyDomainSection({
  domain,
  granted,
}: {
  domain: DomainGroup;
  granted: ReadonlySet<string>;
}) {
  const { t } = useTranslation(["userGroups"]);
  const stats = domainStats(domain, granted);
  const readOnly = domainIsReadOnly(domain);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <span className="text-sm font-semibold text-on-surface">
          {t(`domain.${domain.key}`, { defaultValue: domain.label })}
        </span>
        <span className="text-xs text-muted">
          {"· "}
          {t("matrix.domainCount", {
            granted: stats.granted,
            total: stats.total,
            entities: stats.entities,
          })}
          {readOnly && <span className="ml-1">({t("matrix.readOnly")})</span>}
        </span>
      </div>
      <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="px-4 py-2 font-medium">{t("matrix.colEntity")}</th>
              <th className="px-2 py-2 font-medium">{t("operation.create")}</th>
              <th className="px-2 py-2 font-medium">{t("operation.read")}</th>
              <th className="px-2 py-2 font-medium">{t("operation.update")}</th>
              <th className="px-2 py-2 font-medium">{t("operation.delete")}</th>
              <th className="px-4 py-2 font-medium">{t("matrix.colActionVerbs")}</th>
            </tr>
          </thead>
          <tbody>
            {domain.entities.map((entity) => (
              <ReadOnlyEntityRow key={entity.entity} entity={entity} granted={granted} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

interface Props {
  effective: EffectivePermissions;
  groupCount: number;
}

/**
 * The member's effective-permission matrix rendered READ-ONLY. Reuses the
 * shared permission-matrix MODEL (`permission-matrix-model.ts`: domain
 * grouping, CRUD columns, action verbs, per-domain stats) so the row/column
 * vocabulary is identical to the interactive Groups editor — but every control
 * is disabled and no mutation is ever fired. The grant set is DERIVED from the
 * union of the member's groups (`effective.grants`), never from a single group;
 * editing happens on the Groups surface, not here.
 */
export function MemberEffectiveMatrix({ effective, groupCount }: Props) {
  const { t } = useTranslation(["org"]);
  const { data: registry, isLoading, isError } = usePermissionRegistry();

  const domains = useMemo(() => registry?.domains ?? [], [registry]);
  const granted = useMemo(() => {
    const set = new Set<string>();
    for (const g of effective.grants) {
      for (const op of g.operations) set.add(tokenKey(g.entity, op));
    }
    return set;
  }, [effective.grants]);

  const grantedCount = useMemo(
    () => effective.grants.reduce((n, g) => n + g.operations.length, 0),
    [effective.grants],
  );
  const entityCount = useMemo(() => domains.reduce((n, d) => n + d.entities.length, 0), [domains]);

  if (isLoading) return <SkeletonTable columns={6} rows={6} />;
  if (isError) {
    return (
      <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
        {t("memberDetail.matrixLoadFailed")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-muted">
          {t("memberDetail.effectiveSummary", {
            permissions: grantedCount,
            groups: groupCount,
            domains: domains.length,
            entities: entityCount,
          })}
        </span>
        <Badge variant="warning">{t("memberDetail.readOnlyBadge")}</Badge>
      </div>

      <div className="rounded-md bg-primary/10 p-3 text-sm text-on-surface">
        {t("memberDetail.effectiveNote")}
      </div>

      <div className="space-y-3">
        {domains.map((domain) => (
          <ReadOnlyDomainSection key={domain.key} domain={domain} granted={granted} />
        ))}
      </div>

      <p className="text-xs text-muted">{t("memberDetail.illegalFooter")}</p>
    </div>
  );
}
